import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  Bell,
  Building2,
  CalendarDays,
  CheckCircle2,
  CircleDashed,
  CircleDollarSign,
  Clock,
  Contact,
  FileText,
  Gift,
  HeartPulse,
  History,
  Info,
  Mail,
  MapPin,
  Network,
  Package,
  Pencil,
  Phone,
  Settings,
  Smartphone,
  Store,
  Tag,
  User,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import FilterBar from '../components/FilterBar';
import Pagination from '../components/Pagination';
import RowActions from '../components/RowActions';
import TableHeader from '../components/TableHeader';
import TableZoomControls, { useTableZoom } from '../components/TableZoomControls';
import TenantLegalDocumentsModal from '../components/TenantLegalDocumentsModal';
import CommercialOfferBox from '../components/CommercialOfferBox';
import { activationTrackerService, deviceService, tenantService } from '../services/api';
import './ActivationTracker.css';
import './Tenants.css';

const ColumnLabel = ({ icon: Icon, children }) => (
  <span className="tenant-column-label">
    <Icon size={14} aria-hidden="true" />
    <span>{children}</span>
  </span>
);

const Tenants = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const tableZoom = useTableZoom();

  // Sorting state
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  // Filters state
  const [filters, setFilters] = useState({
    status: '',
    hasTotem: '',
    minLocals: '',
    minWorkers: '',
    hideSuspended: true,
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modal state
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activatingTenant, setActivatingTenant] = useState(null);
  const [statusChangeModal, setStatusChangeModal] = useState({ open: false, tenant: null, newStatus: null, suspensionNote: '' });
  const [legalDocumentsModal, setLegalDocumentsModal] = useState({ open: false, tenantId: null });
  const [editingAliasTenantId, setEditingAliasTenantId] = useState(null);
  const [aliasDraft, setAliasDraft] = useState('');
  const [savingAlias, setSavingAlias] = useState(false);
  const [activationByTenantId, setActivationByTenantId] = useState({});
  const [activationSummary, setActivationSummary] = useState(null);
  const [activationFilter, setActivationFilter] = useState('');
  const [trackerDrawerOpen, setTrackerDrawerOpen] = useState(false);
  const [trackerTenantId, setTrackerTenantId] = useState(null);
  const [trackerDetail, setTrackerDetail] = useState(null);
  const [trackerDetailLoading, setTrackerDetailLoading] = useState(false);
  const [trackerDetailError, setTrackerDetailError] = useState('');
  const [sectionActionLoadingKey, setSectionActionLoadingKey] = useState('');
  const [contractReminderPreviewModal, setContractReminderPreviewModal] = useState({
    open: false,
    loading: false,
    sending: false,
    data: null,
    stepKey: '',
    reminderKind: 'contract',
    context: null,
    additionalRecipients: '',
    error: '',
  });
  const [snapshotTooltipOpen, setSnapshotTooltipOpen] = useState(false);
  const [hoveredSnapshotCard, setHoveredSnapshotCard] = useState(null);
  const [hoveredReminderTooltipTenantId, setHoveredReminderTooltipTenantId] = useState(null);
  const [contactCardTenant, setContactCardTenant] = useState(null);
  const [contactCardLoading, setContactCardLoading] = useState(false);
  const [contactCardError, setContactCardError] = useState('');

  useEffect(() => {
    loadTenants();
  }, []);

  // Reset to first page when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filters, activationFilter]);

  const loadTenants = async () => {
    try {
      setLoading(true);
      setError(null);

      // Usa nuovo endpoint admin che include già le stats
      const [tenantsData, trackerResponse] = await Promise.all([
        tenantService.getTenants(),
        activationTrackerService.getTenants({ includeLive: true }),
      ]);

      setTenants(tenantsData);

      const trackerItems = trackerResponse?.data?.items || [];
      setActivationSummary(trackerResponse?.data?.summary || null);
      const nextActivationByTenantId = trackerItems.reduce((acc, item) => {
        if (!item?.tenantId) return acc;
        const lastReminder = item?.reminderTracking?.lastAny || null;
        acc[item.tenantId] = {
          activationStatus: item.activationStatus,
          isOperationallyBlocked: Boolean(item.isOperationallyBlocked),
          progressPercent: Number.isFinite(item.progressPercent) ? item.progressPercent : 0,
          mainBlocker: item.mainBlocker || '',
          daysInCurrentStep: item.daysInCurrentStep,
          blockerSeverity: item.blockerSeverity || 'low',
          lastReminderAt: lastReminder?.sentAt || null,
          lastReminderType: (lastReminder?.triggerType || '').toLowerCase(),
          lastReminderBy: lastReminder?.initiatedBy?.email
            || lastReminder?.initiatedBy?.displayName
            || lastReminder?.initiatedBy?.source
            || '',
          lastTipReceivedAt: item?.lastTipReceivedAt || null,
          lastTipReceivedAmount: item?.lastTipReceivedAmount ?? null,
          lastTipReceivedCurrency: item?.lastTipReceivedCurrency || 'EUR',
        };
        return acc;
      }, {});
      setActivationByTenantId(nextActivationByTenantId);
    } catch (err) {
      console.error('Failed to load tenants:', err);
      setError(err.message);
      setActivationByTenantId({});
      setActivationSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const openTrackerDrawer = (tenantId) => {
    setTrackerTenantId(tenantId);
    setTrackerDrawerOpen(true);
  };

  const closeTrackerDrawer = () => {
    setTrackerDrawerOpen(false);
  };

  const openContactCard = async (tenant, userEmail, businessEmail, phoneNumber) => {
    const ownerFullName = [tenant.owner?.first_name, tenant.owner?.last_name]
      .filter(Boolean)
      .join(' ')
      .trim() || '-';

    setContactCardTenant({
      id: tenant.id,
      companyName: tenant.company_name,
      ownerFullName,
      userEmail,
      businessEmail,
      phoneNumber,
      tenantStatus: tenant.status,
      pIva: tenant.p_iva,
      venues: [],
    });

    setContactCardLoading(true);
    setContactCardError('');
    try {
      const detail = await tenantService.getTenant(tenant.id);
      const venues = Array.isArray(detail?.venues) ? detail.venues : [];
      const normalizedVenues = venues.map((venue) => ({
        id: venue.id,
        name: venue.name,
        address: venue.address || '-',
      }));

      setContactCardTenant((prev) => {
        if (!prev || prev.id !== tenant.id) return prev;
        return {
          ...prev,
          venues: normalizedVenues,
        };
      });
    } catch (err) {
      setContactCardError(err?.response?.data?.error || err.message || 'Errore caricamento locali.');
    } finally {
      setContactCardLoading(false);
    }
  };

  const closeContactCard = () => {
    setContactCardTenant(null);
    setContactCardLoading(false);
    setContactCardError('');
  };

  useEffect(() => {
    if (!trackerDrawerOpen || !trackerTenantId) {
      setTrackerDetail(null);
      return;
    }

    let cancelled = false;
    const loadTrackerDetail = async () => {
      setTrackerDetailLoading(true);
      setTrackerDetailError('');
      try {
        const response = await activationTrackerService.getTenant(trackerTenantId);
        if (!cancelled) setTrackerDetail(response?.data || null);
      } catch (err) {
        if (!cancelled) {
          setTrackerDetail(null);
          setTrackerDetailError(err?.response?.data?.message || err.message || 'Errore caricamento tracker.');
        }
      } finally {
        if (!cancelled) setTrackerDetailLoading(false);
      }
    };

    loadTrackerDetail();
    return () => {
      cancelled = true;
    };
  }, [trackerDrawerOpen, trackerTenantId]);

  const normalizeActivationStatus = (status) => String(status || '').toLowerCase();

  const STEP_STATUS_LABELS = {
    not_started: 'Non iniziato',
    in_progress: 'In corso',
    completed: 'Completato',
    blocked: 'Bloccato',
    unknown: 'Non disponibile',
  };

  const BLOCKER_TYPE_LABELS = {
    operational_blocker: 'Bloccante',
    missing_data: 'Dato mancante',
    warning: 'Da verificare',
    info: 'Info',
    none: 'OK',
  };

  const BLOCKER_TYPE_TITLES = {
    operational_blocker: 'Problema che impedisce realmente attivazione o go-live.',
    missing_data: 'Dato non trovato: potrebbe essere storico/non tracciato.',
    warning: 'Situazione da verificare, ma non necessariamente bloccante.',
    info: 'Informazione operativa.',
    none: 'Nessun blocco rilevato.',
  };

  const DEVICE_STATUS_LABELS = {
    PROVISIONED: 'Provisionato',
    SHIPPED_PENDING_ACTIVATION: 'Shipped/Pending activation',
    ACTIVE: 'Attivo',
    DISABLED: 'Disabilitato',
  };

  const formatDate = (value) => {
    if (!value) return 'Non disponibile';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Non disponibile';
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateTime = (value) => {
    if (!value) return 'Non disponibile';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Non disponibile';
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRelativeTime = (value) => {
    if (!value) return 'mai inviato';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'data non disponibile';

    const diffMs = Date.now() - date.getTime();
    if (diffMs < 0) return 'tra poco';

    const minuteMs = 60 * 1000;
    const hourMs = 60 * minuteMs;
    const dayMs = 24 * hourMs;

    if (diffMs < minuteMs) return 'adesso';

    const minutes = Math.floor(diffMs / minuteMs);
    if (minutes < 60) return `${minutes} min fa`;

    const hours = Math.floor(diffMs / hourMs);
    if (hours < 24) return hours === 1 ? '1 ora fa' : `${hours} ore fa`;

    const days = Math.floor(diffMs / dayMs);
    if (days < 30) return days === 1 ? '1 giorno fa' : `${days} giorni fa`;

    const months = Math.floor(days / 30);
    if (months < 12) return months === 1 ? '1 mese fa' : `${months} mesi fa`;

    const years = Math.floor(days / 365);
    return years === 1 ? '1 anno fa' : `${years} anni fa`;
  };

  const formatRelativeTimeCompact = (value) => {
    if (!value) return 'mai';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'n/d';

    const diffMs = Date.now() - date.getTime();
    if (diffMs < 0) return 'tra poco';

    const minuteMs = 60 * 1000;
    const hourMs = 60 * minuteMs;
    const dayMs = 24 * hourMs;

    if (diffMs < minuteMs) return 'ora';

    const minutes = Math.floor(diffMs / minuteMs);
    if (minutes < 60) return `${minutes}m fa`;

    const hours = Math.floor(diffMs / hourMs);
    if (hours < 24) return `${hours}h fa`;

    const days = Math.floor(diffMs / dayMs);
    if (days < 30) return `${days}g fa`;

    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mes fa`;

    const years = Math.floor(days / 365);
    return `${years}a fa`;
  };

  const getReminderTypeLabel = (type) => {
    if (type === 'automatic') return 'Automatico';
    if (type === 'manual') return 'Manuale';
    return 'N/D';
  };

  const formatDays = (days, fallbackLabel) => {
    if (fallbackLabel) return fallbackLabel;
    if (days === null || days === undefined) return 'N/D';
    if (days === 0) return 'Oggi';
    if (days === 1) return '1 giorno';
    return `${days} giorni`;
  };

  const formatCurrencyCents = (valueInCents) => {
    if (valueInCents === null || valueInCents === undefined || Number.isNaN(Number(valueInCents))) {
      return 'N/D';
    }

    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(valueInCents) / 100);
  };

  const formatCurrencyAmount = (value, currency = 'EUR') => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return 'N/D';
    }

    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: currency || 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value));
  };

  const StepIcon = ({ status }) => {
    if (status === 'completed') return <CheckCircle2 size={18} color="#16a34a" strokeWidth={2.4} />;
    if (status === 'blocked') return <XCircle size={18} />;
    if (status === 'in_progress') return <Clock size={18} />;
    if (status === 'unknown') return <AlertTriangle size={18} />;
    return <CircleDashed size={18} />;
  };

  const SignalBadge = ({ type = 'none' }) => {
    const normalized = type || 'none';
    return (
      <span className={`at-signal-badge at-signal-badge--${normalized}`} title={BLOCKER_TYPE_TITLES[normalized] || normalized}>
        {BLOCKER_TYPE_LABELS[normalized] || normalized}
      </span>
    );
  };

  const getActivationStatusBadge = (status) => {
    const normalizedStatus = normalizeActivationStatus(status);
    const statusConfig = {
      live: { color: '#198754', label: 'Live' },
      ready_for_go_live: { color: '#20c997', label: 'Pronto go live' },
      workers_setup_required: { color: '#fd7e14', label: 'Setup worker' },
      collection_channel_setup_required: {
        color: '#fd7e14',
        label: 'Setup Canale',
        title: 'setup canale di incasso (Kiosk\\QR)',
      },
      approved_setup_required: { color: '#0d6efd', label: 'Creazione locale' },
      pending_soontip_approval: { color: '#6f42c1', label: 'Pending approval' },
      contract_pending: { color: '#e67e22', label: 'Contratto pending' },
      onboarding_incomplete: { color: '#6c757d', label: 'Onboarding incompleto' },
      registered: { color: '#6c757d', label: 'Registrato' },
      suspended_closed: { color: '#dc3545', label: 'Suspended/Closed' },
      unknown: { color: '#6c757d', label: 'N/D' },
    };

    const config = statusConfig[normalizedStatus] || { color: '#6c757d', label: status || 'N/D' };
    return (
      <span
        style={{
          backgroundColor: config.color,
          color: 'white',
          padding: '3px 7px',
          borderRadius: '12px',
          fontSize: '11px',
          fontWeight: 700,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          whiteSpace: 'nowrap',
          maxWidth: '120px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          verticalAlign: 'middle',
        }}
        title={config.title || config.label}
      >
        {config.label}
        {normalizedStatus === 'collection_channel_setup_required' && <CircleDollarSign size={12} aria-hidden="true" />}
      </span>
    );
  };

  const openStatusChangeModal = (tenant, newStatus) => {
    setStatusChangeModal({ open: true, tenant, newStatus, suspensionNote: '' });
  };
  const closeStatusChangeModal = () => {
    setStatusChangeModal({ open: false, tenant: null, newStatus: null, suspensionNote: '' });
  };

  const handleChangeStatus = async () => {
    const { tenant, newStatus, suspensionNote } = statusChangeModal;
    if (!tenant || !newStatus) return;

    try {
      await tenantService.updateTenantStatus(tenant.id, newStatus, `Cambio status a ${newStatus} da admin dashboard`, suspensionNote);
      alert('Status cambiato con successo!');
      closeStatusChangeModal();
      loadTenants();
    } catch (err) {
      alert(`Errore: ${err.message}`);
    }
  };

  const openActivateModal = (tenant) => {
    setActivatingTenant(tenant);
    setActivateModalOpen(true);
  };

  const closeActivateModal = () => {
    setActivateModalOpen(false);
    setActivatingTenant(null);
  };

  const handleActivateTenant = async () => {
    if (!activatingTenant) return;

    try {
      await tenantService.updateTenantStatus(activatingTenant.id, 'ACTIVE', 'Attivazione tenant da admin dashboard');
      alert('Tenant attivato con successo.');
      closeActivateModal();
      loadTenants();
    } catch (err) {
      alert(`Errore: ${err.message}`);
    }
  };

  const handleDeleteTenant = async (tenantId) => {
    if (!window.confirm('ATTENZIONE: Eliminare il tenant? Questa azione è irreversibile!')) {
      return;
    }

    const reason = prompt('Inserisci motivo eliminazione:');
    if (!reason) return;

    try {
      await tenantService.deleteTenant(tenantId, reason);
      alert('Tenant eliminato con successo!');
      loadTenants();
    } catch (err) {
      const apiError = err?.response?.data?.error;
      const details = err?.response?.data?.details;
      const detailText = details
        ? `\nDettagli: venue=${details.venues ?? 0}, worker=${details.workers ?? 0}, device=${details.devices ?? 0}`
        : '';
      alert(`Errore: ${apiError || err.message}${detailText}`);
    }
  };

  const openLegalDocumentsModal = (tenant) => {
    setLegalDocumentsModal({ open: true, tenantId: tenant.id });
  };

  const closeLegalDocumentsModal = () => {
    setLegalDocumentsModal({ open: false, tenantId: null });
  };

  const startAliasEdit = (tenant) => {
    setEditingAliasTenantId(tenant.id);
    setAliasDraft(tenant.adminAlias || '');
  };

  const cancelAliasEdit = () => {
    setEditingAliasTenantId(null);
    setAliasDraft('');
  };

  const saveAliasEdit = async (tenantId) => {
    try {
      setSavingAlias(true);
      const response = await tenantService.updateTenantAlias(tenantId, aliasDraft);
      const updatedAlias = response?.tenant?.adminAlias || null;

      setTenants(prev => prev.map(t => (
        t.id === tenantId ? { ...t, adminAlias: updatedAlias } : t
      )));

      setEditingAliasTenantId(null);
      setAliasDraft('');
    } catch (err) {
      const apiError = err?.response?.data?.error;
      alert(`Errore aggiornamento alias: ${apiError || err.message}`);
    } finally {
      setSavingAlias(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'REGISTERED': { color: '#6c757d', label: 'Registrato' },
      'EMAIL_VERIFIED': { color: '#17a2b8', label: 'Email Verificata' },
      'ONBOARDING_STARTED': { color: '#ffc107', label: 'Onboarding' },
      'CONSENTS_ACCEPTED': { color: '#fd7e14', label: 'Consensi OK' },
      'CONTRACT_SIGNED_PENDING_PAYMENT':     { color: '#d97706', label: 'In Attesa Pagamento' },
      'CONTRACT_SIGNED_PENDING_ACTIVATION': { color: '#e83e8c', label: 'In Attesa Attivazione' },
      'ACTIVE': { color: '#28a745', label: 'Attivo' },
      'SUSPENDED': { color: '#dc3545', label: 'Sospeso' },
      'CLOSED': { color: '#6c757d', label: 'Chiuso' }
    };

    const config = statusConfig[status] || { color: '#6c757d', label: status || 'UNKNOWN' };

    return (
      <span className="tenant-status-badge" style={{
        backgroundColor: config.color,
        color: 'white',
      }}>
        {config.label}
      </span>
    );
  };

  // Sorting handler
  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  // Filter handler
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Compute filtered and sorted tenants
  const filteredAndSortedTenants = useMemo(() => {
    let result = [...tenants];

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(tenant =>
        (tenant.adminAlias || '').toLowerCase().includes(query) ||
        (tenant.company_name || '').toLowerCase().includes(query) ||
        (tenant.contact_email || '').toLowerCase().includes(query) ||
        (tenant.email || '').toLowerCase().includes(query) ||
        (tenant.contact_phone || '').toLowerCase().includes(query) ||
        (tenant.phone || '').toLowerCase().includes(query) ||
        (tenant.id || '').toLowerCase().includes(query)
      );
    }

    // Apply filters
    if (filters.status) {
      result = result.filter(t => t.status === filters.status);
    }

    if (filters.hasTotem === 'yes') {
      result = result.filter(t => (t.stats?.devices || 0) >= 1);
    } else if (filters.hasTotem === 'no') {
      result = result.filter(t => (t.stats?.devices || 0) === 0);
    }

    if (filters.minLocals) {
      const min = parseInt(filters.minLocals, 10);
      result = result.filter(t => (t.stats?.venues || 0) >= min);
    }

    if (filters.minWorkers) {
      const min = parseInt(filters.minWorkers, 10);
      result = result.filter(t => (t.stats?.workers || 0) >= min);
    }

    if (filters.hideSuspended) {
      result = result.filter((tenant) => String(tenant.status || '').toUpperCase() !== 'SUSPENDED');
    }

    if (activationFilter) {
      result = result.filter((tenant) => {
        const activation = activationByTenantId[tenant.id];
        const normalized = normalizeActivationStatus(activation?.activationStatus);
        if (activationFilter === 'non_live') {
          return normalized && normalized !== 'live';
        }
        if (activationFilter === 'blocked_operational') {
          return Boolean(activation?.isOperationallyBlocked);
        }
        if (activationFilter === 'blocked_week') {
          return Boolean(activation?.isOperationallyBlocked) && Number(activation?.daysInCurrentStep) >= 7;
        }
        return normalized === activationFilter;
      });
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal, bVal;

      switch (sortColumn) {
        case 'alias':
          aVal = (a.adminAlias || '').toLowerCase();
          bVal = (b.adminAlias || '').toLowerCase();
          break;
        case 'company_name':
          aVal = (a.company_name || '').toLowerCase();
          bVal = (b.company_name || '').toLowerCase();
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt || 0).getTime();
          bVal = new Date(b.createdAt || 0).getTime();
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        case 'operational_footprint':
          aVal = (a.stats?.venues || 0) + (a.stats?.teams || 0) + (a.stats?.workers || 0) + (a.stats?.devices || 0);
          bVal = (b.stats?.venues || 0) + (b.stats?.teams || 0) + (b.stats?.workers || 0) + (b.stats?.devices || 0);
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [tenants, searchQuery, filters, sortColumn, sortDirection, activationFilter, activationByTenantId]);

  const trackerTenant = useMemo(
    () => tenants.find((tenant) => tenant.id === trackerTenantId) || null,
    [tenants, trackerTenantId]
  );

  // Compute paginated tenants
  const paginatedTenants = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedTenants.slice(startIndex, endIndex);
  }, [filteredAndSortedTenants, currentPage, itemsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const activationCards = useMemo(() => ([
    {
      key: 'activation',
      label: 'In attivazione',
      value: activationSummary?.activation ?? 0,
      color: '#fd7e14',
      filterValue: 'non_live',
      description: 'Tutti i tenant non-live, inclusi quelli pronti per andare live.',
    },
    {
      key: 'blocked',
      label: 'Bloccati',
      value: activationSummary?.blocked ?? 0,
      color: '#dc3545',
      filterValue: 'blocked_operational',
      description: 'Tenant in attivazione con blocco operativo reale, esclusi i pronti al go-live (pronti ma senza prima mancia).',
    },
    {
      key: 'blocked_week',
      label: 'Bloccati 7g+',
      value: Object.values(activationByTenantId).filter((item) => (
        Boolean(item?.isOperationallyBlocked) && Number(item?.daysInCurrentStep) >= 7
      )).length,
      color: '#b91c1c',
      filterValue: 'blocked_week',
      description: 'Tenant con blocco operativo reale, fermi da almeno 7 giorni.',
    },
    {
      key: 'pending',
      label: 'Pending approval',
      value: activationSummary?.pendingApproval ?? 0,
      color: '#6f42c1',
      filterValue: 'pending_soontip_approval',
      description: 'In attesa di approvazione manuale SoonTip.',
    },
    {
      key: 'ready',
      label: 'Pronti go-live',
      value: activationSummary?.readyForGoLive ?? 0,
      color: '#20c997',
      filterValue: 'ready_for_go_live',
      description: 'Setup completo ma nessuna transazione riuscita.',
    },
    {
      key: 'live',
      label: 'Live',
      value: activationSummary?.live ?? 0,
      color: '#198754',
      filterValue: 'live',
      description: 'Operativo: setup completo + almeno una transazione.',
    },
  ]), [activationSummary, activationByTenantId]);

  const handleSectionStepAction = async (section, step) => {
    if (!trackerTenantId || !step?.actionType || !step?.isActionAvailable) return;

    const actionKey = `${section?.key || 'section'}:${step?.key || 'step'}:${trackerTenantId}`;
    setSectionActionLoadingKey(actionKey);
    try {
      if (step.actionType === 'send_contract_reminder') {
        setContractReminderPreviewModal({
          open: true,
          loading: true,
          sending: false,
          data: null,
          stepKey: step?.key || '',
          reminderKind: 'contract',
          context: null,
          additionalRecipients: '',
          error: '',
        });
        const response = await activationTrackerService.previewContractReminder(trackerTenantId, step?.key || '');
        const preview = response?.data || null;
        setContractReminderPreviewModal({
          open: true,
          loading: false,
          sending: false,
          data: preview,
          stepKey: step?.key || '',
          reminderKind: 'contract',
          context: null,
          additionalRecipients: '',
          error: '',
        });
        return;
      }

      if (step.actionType === 'send_venue_setup_reminder') {
        setContractReminderPreviewModal({
          open: true,
          loading: true,
          sending: false,
          data: null,
          stepKey: step?.key || '',
          reminderKind: 'venue',
          context: null,
          additionalRecipients: '',
          error: '',
        });
        const response = await activationTrackerService.previewVenueReminder(trackerTenantId, step?.key || '');
        const preview = response?.data || null;
        setContractReminderPreviewModal({
          open: true,
          loading: false,
          sending: false,
          data: preview,
          stepKey: step?.key || '',
          reminderKind: 'venue',
          context: null,
          additionalRecipients: '',
          error: '',
        });
        return;
      }

      if (step.actionType === 'activate_tenant') {
        if (!trackerTenant) {
          alert('Tenant non trovato nella lista corrente. Ricarica la pagina e riprova.');
          return;
        }

        openActivateModal(trackerTenant);
        return;
      }
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Errore esecuzione azione.';
      alert(message);
    } finally {
      setSectionActionLoadingKey('');
    }
  };

  const handleSingleVenueDeviceAction = async (venue, device) => {
    if (!trackerTenantId || !device?.id) return;

    const status = String(device?.status || '').toUpperCase();
    const actionKey = `venue:${venue?.venueId || venue?.venueName || 'unknown'}:device:${device.id}:${trackerTenantId}`;
    setSectionActionLoadingKey(actionKey);

    try {
      if (status === 'PROVISIONED') {
        await deviceService.updateDeviceStatus(
          device.id,
          'SHIPPED_PENDING_ACTIVATION',
          `Cambio stato da Activation Tracker per device ${device.label || device.setupCode || device.id}`
        );

        const refreshed = await activationTrackerService.getTenant(trackerTenantId);
        setTrackerDetail(refreshed?.data || null);
        await loadTenants();
        alert('Status device aggiornato a Spedito/Pending activation.');
        return;
      }

      if (status === 'SHIPPED_PENDING_ACTIVATION') {
        setContractReminderPreviewModal({
          open: true,
          loading: true,
          sending: false,
          data: null,
          stepKey: 'device_activation',
          reminderKind: 'device_activation',
          context: {
            venueId: venue?.venueId || '',
            venueName: venue?.venueName || '',
            deviceId: device?.id || '',
          },
          additionalRecipients: '',
          error: '',
        });

        const response = await activationTrackerService.previewDeviceActivationReminder(
          trackerTenantId,
          venue?.venueId || '',
          device?.id || ''
        );
        const preview = response?.data || null;

        setContractReminderPreviewModal({
          open: true,
          loading: false,
          sending: false,
          data: preview,
          stepKey: 'device_activation',
          reminderKind: 'device_activation',
          context: {
            venueId: venue?.venueId || '',
            venueName: venue?.venueName || '',
            deviceId: device?.id || '',
          },
          additionalRecipients: '',
          error: '',
        });
      }
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Errore azione device.';
      alert(message);
    } finally {
      setSectionActionLoadingKey('');
    }
  };

  const handleWorkerCheckReminderAction = async (venue, check) => {
    if (!trackerTenantId) return;
    if (!check?.isActionAvailable || check?.actionType !== 'send_worker_onboarding_reminder') return;

    const actionKey = `venue:${venue?.venueId || venue?.venueName || 'unknown'}:worker:${check?.actionStepKey || 'default'}:${trackerTenantId}`;
    setSectionActionLoadingKey(actionKey);
    try {
      setContractReminderPreviewModal({
        open: true,
        loading: true,
        sending: false,
        data: null,
        stepKey: check?.actionStepKey || 'worker_onboarding',
        reminderKind: 'worker',
        context: {
          venueId: venue?.venueId || check?.actionVenueId || '',
          venueName: venue?.venueName || check?.actionVenueName || '',
          stepKey: check?.actionStepKey || 'worker_onboarding',
        },
        additionalRecipients: '',
        error: '',
      });

      const response = await activationTrackerService.previewWorkerOnboardingReminder(
        trackerTenantId,
        venue?.venueId || check?.actionVenueId || '',
        check?.actionStepKey || 'worker_onboarding'
      );
      const preview = response?.data || null;

      setContractReminderPreviewModal({
        open: true,
        loading: false,
        sending: false,
        data: preview,
        stepKey: check?.actionStepKey || 'worker_onboarding',
        reminderKind: 'worker',
        context: {
          venueId: venue?.venueId || check?.actionVenueId || '',
          venueName: venue?.venueName || check?.actionVenueName || '',
          stepKey: check?.actionStepKey || 'worker_onboarding',
        },
        additionalRecipients: '',
        error: '',
      });
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Errore caricamento reminder lavoratori.';
      alert(message);
    } finally {
      setSectionActionLoadingKey('');
    }
  };

  const closeContractReminderPreview = () => {
    setContractReminderPreviewModal({
      open: false,
      loading: false,
      sending: false,
      data: null,
      stepKey: '',
      reminderKind: 'contract',
      context: null,
      additionalRecipients: '',
      error: '',
    });
  };

  const handleSendContractReminderFromPreview = async () => {
    if (!trackerTenantId) return;

    setContractReminderPreviewModal((prev) => ({ ...prev, sending: true, error: '' }));
    try {
      if (contractReminderPreviewModal.reminderKind === 'venue') {
        await activationTrackerService.sendVenueReminder(trackerTenantId, {
          additionalRecipients: contractReminderPreviewModal.additionalRecipients,
        }, contractReminderPreviewModal.stepKey || '');
      } else if (contractReminderPreviewModal.reminderKind === 'device_activation') {
        await activationTrackerService.sendDeviceActivationReminder(trackerTenantId, {
          additionalRecipients: contractReminderPreviewModal.additionalRecipients,
        }, contractReminderPreviewModal.context?.venueId || '', contractReminderPreviewModal.context?.deviceId || '');
      } else if (contractReminderPreviewModal.reminderKind === 'worker') {
        await activationTrackerService.sendWorkerOnboardingReminder(trackerTenantId, {
          additionalRecipients: contractReminderPreviewModal.additionalRecipients,
        }, contractReminderPreviewModal.context?.venueId || '', contractReminderPreviewModal.context?.stepKey || 'worker_onboarding');
      } else {
        await activationTrackerService.sendContractReminder(trackerTenantId, {
          additionalRecipients: contractReminderPreviewModal.additionalRecipients,
        }, contractReminderPreviewModal.stepKey || '');
      }
      alert('Email reminder inviata con successo (IT/EN).');
      closeContractReminderPreview();
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Errore invio reminder.';
      setContractReminderPreviewModal((prev) => ({ ...prev, sending: false, error: message }));
    }
  };

  // Table columns configuration
  const columns = [
    { key: 'alias', label: <ColumnLabel icon={Tag}>Alias</ColumnLabel>, width: '13%', sortable: true },
    { key: 'company_name', label: <ColumnLabel icon={Building2}>Tenant</ColumnLabel>, width: '18%', sortable: true },
    { key: 'createdAt', label: <ColumnLabel icon={CalendarDays}>Data<br />Registrazione</ColumnLabel>, width: '10%', sortable: true },
    { key: 'status', label: <ColumnLabel icon={BarChart3}>Status</ColumnLabel>, width: '11%', sortable: true },
    { key: 'plan', label: <ColumnLabel icon={Package}>Abbonamento</ColumnLabel>, width: '13%', sortable: false },
    { key: 'activation', label: <ColumnLabel icon={HeartPulse}>Tracker</ColumnLabel>, width: '13%', sortable: false },
    { key: 'operational_footprint', label: <ColumnLabel icon={Network}>Struttura operativa</ColumnLabel>, width: '11%', sortable: true },
    { key: 'actions', label: <ColumnLabel icon={Settings}>Azioni</ColumnLabel>, width: '7%', sortable: false }
  ];

  if (loading) return <div className="loading">Caricamento dashboard tenant...</div>;

  return (
    <div className="tenants-page">
      <header className="tenants-page-header">
        <span className="tenants-page-header-icon">
          <Building2 size={21} aria-hidden="true" />
        </span>
        <div>
          <h1>Gestione tenant</h1>
          <p>Visualizza e gestisci tenant, attivazioni e abbonamenti.</p>
          {error && <div className="error">{error}</div>}
        </div>
      </header>

      <div className="card tenants-snapshot-card">
        <div style={{ marginBottom: '14px', color: '#334155', fontSize: '13px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>Activation Tracker Snapshot</span>
          <span
            aria-label="Spiegazione stati tracker"
            style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
            onMouseEnter={() => setSnapshotTooltipOpen(true)}
            onMouseLeave={() => setSnapshotTooltipOpen(false)}
            onFocus={() => setSnapshotTooltipOpen(true)}
            onBlur={() => setSnapshotTooltipOpen(false)}
          >
            <button
              type="button"
              style={{
                border: 'none',
                background: 'transparent',
                color: '#94a3b8',
                padding: 0,
                margin: 0,
                display: 'inline-flex',
                cursor: 'help',
              }}
              aria-label="Mostra descrizione stati tracker"
            >
              <Info size={13} />
            </button>

            {snapshotTooltipOpen && (
              <span
                role="tooltip"
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '320px',
                  maxWidth: 'min(320px, 80vw)',
                  background: '#0f172a',
                  color: '#ffffff',
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: 1.35,
                  padding: '10px 12px',
                  borderRadius: '8px',
                  boxShadow: '0 8px 20px rgba(15, 23, 42, 0.35)',
                  zIndex: 100,
                }}
              >
                In attivazione = tutti i tenant non-live.<br />
                Bloccati = tenant in attivazione con blocco operativo reale, esclusi quelli pronti per andare live.<br />
                Ready for go-live = setup completo ma senza transazioni riuscite (pronti al go-live).
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: '-6px',
                    left: '50%',
                    transform: 'translateX(-50%) rotate(45deg)',
                    width: '12px',
                    height: '12px',
                    background: '#0f172a',
                  }}
                />
              </span>
            )}
          </span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '8px',
            marginBottom: 0,
          }}
        >
          {activationCards.map((card) => (
            <button
              key={card.key}
              type="button"
              onClick={() => setActivationFilter(card.filterValue)}
              onMouseEnter={() => setHoveredSnapshotCard(card.key)}
              onMouseLeave={() => setHoveredSnapshotCard(null)}
              onFocus={() => setHoveredSnapshotCard(card.key)}
              onBlur={() => setHoveredSnapshotCard(null)}
              style={{
                border: activationFilter === card.filterValue ? `1px solid ${card.color}` : '1px solid #e5e7eb',
                borderRadius: '10px',
                background: activationFilter === card.filterValue || hoveredSnapshotCard === card.key
                  ? `${card.color}12`
                  : '#fff',
                textAlign: 'left',
                padding: '10px 12px',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'visible',
                boxShadow: activationFilter === card.filterValue ? `0 0 0 2px ${card.color}20` : 'none',
                transform: hoveredSnapshotCard === card.key ? 'translateY(-1px)' : 'none',
                transition: 'background-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease',
              }}
              title={`${card.description} Clicca per filtrare.`}
            >
              <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>{card.label}</span>
                <span aria-label={card.description} title={card.description} style={{ display: 'inline-flex', color: '#94a3b8' }}>
                  <Info size={12} />
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: '24px', lineHeight: 1 }}>{card.value}</strong>
                <span
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: card.color,
                    display: 'inline-block',
                  }}
                />
              </div>

              {hoveredSnapshotCard === card.key && (
                <span
                  role="tooltip"
                  style={{
                    position: 'absolute',
                    left: '50%',
                    bottom: 'calc(100% + 10px)',
                    transform: 'translateX(-50%)',
                    width: '280px',
                    maxWidth: 'min(280px, 75vw)',
                    background: '#0f172a',
                    color: '#fff',
                    padding: '8px 10px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    lineHeight: 1.35,
                    fontWeight: 500,
                    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.35)',
                    zIndex: 120,
                    pointerEvents: 'none',
                  }}
                >
                  {card.description}
                  <span
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 0,
                      height: 0,
                      borderLeft: '6px solid transparent',
                      borderRight: '6px solid transparent',
                      borderTop: '6px solid #0f172a',
                    }}
                  />
                </span>
              )}
            </button>
          ))}
        </div>

      </div>

      <FilterBar
        onFilterChange={handleFilterChange}
        totalCount={tenants.length}
        filteredCount={filteredAndSortedTenants.length}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        activationFilter={activationFilter}
        onActivationFilterChange={setActivationFilter}
      />

      <div className="card table-scroll-card">
        <TableZoomControls {...tableZoom} />
        <div className="table-container table-scroll-frame">
          <table
            className="admin-table tenants-table-compact mobile-zoom-table"
            style={{ '--mobile-table-zoom': `${tableZoom.zoom}%` }}
          >
            <TableHeader
              columns={columns}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
            <tbody>
              {paginatedTenants.map(tenant => {
                const stats = tenant.stats || { venues: 0, teams: 0, workers: 0, devices: 0 };
                const activation = activationByTenantId[tenant.id];
                const isOperationallyBlocked = Boolean(activation?.isOperationallyBlocked);
                const progressPercent = Math.max(0, Math.min(100, Math.round(activation?.progressPercent || 0)));
                const normalizedActivationStatus = normalizeActivationStatus(activation?.activationStatus);
                const userEmail = tenant.owner?.email || '-';
                const businessEmail = tenant.contact_email || tenant.email || '-';
                const phoneNumber = tenant.contact_phone || tenant.phone || '-';

                return (
                  <tr key={tenant.id}>
                    <td>
                      <div style={{ maxWidth: '220px', wordWrap: 'break-word' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minHeight: '24px' }}>
                        {editingAliasTenantId === tenant.id ? (
                          <>
                            <input
                              type="text"
                              value={aliasDraft}
                              onChange={(e) => setAliasDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  saveAliasEdit(tenant.id);
                                }
                                if (e.key === 'Escape') {
                                  cancelAliasEdit();
                                }
                              }}
                              disabled={savingAlias}
                              placeholder="Inserisci alias"
                              style={{
                                padding: '3px 6px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '12px',
                                minWidth: '120px',
                                flex: 1
                              }}
                            />
                            <button
                              onClick={() => saveAliasEdit(tenant.id)}
                              disabled={savingAlias}
                              style={{
                                border: 'none',
                                background: '#28a745',
                                color: 'white',
                                borderRadius: '50%',
                                cursor: savingAlias ? 'not-allowed' : 'pointer',
                                width: '22px',
                                height: '22px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                lineHeight: 1
                              }}
                              title="Salva alias"
                            >
                              <CheckCircle2 size={13} aria-hidden="true" />
                            </button>
                            <button
                              onClick={cancelAliasEdit}
                              disabled={savingAlias}
                              style={{
                                border: '1px solid #ddd',
                                background: 'white',
                                color: '#495057',
                                borderRadius: '50%',
                                cursor: savingAlias ? 'not-allowed' : 'pointer',
                                width: '22px',
                                height: '22px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                lineHeight: 1
                              }}
                              title="Annulla"
                            >
                              <X size={13} aria-hidden="true" />
                            </button>
                          </>
                        ) : (
                          <>
                            <small style={{ color: '#495057', margin: 0 }}>
                              <strong>{tenant.adminAlias || '-'}</strong>
                            </small>
                            <button
                              onClick={() => startAliasEdit(tenant)}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                color: '#495057',
                                cursor: 'pointer',
                                padding: '0',
                                fontSize: '13px',
                                lineHeight: 1
                              }}
                              title="Modifica alias"
                              aria-label="Modifica alias"
                            >
                              <Pencil size={13} aria-hidden="true" />
                            </button>
                          </>
                        )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="tenant-cell" style={{ maxWidth: '260px', wordWrap: 'break-word' }}>
                        <strong style={{ display: 'block', marginBottom: '2px' }}>
                          {tenant.isLegacyTenant ? (
                            <span
                              title="Legacy tenant"
                              aria-label="Legacy tenant"
                              style={{ marginRight: 6, color: '#b45309', verticalAlign: 'middle', display: 'inline-flex' }}
                            >
                              <History size={13} aria-hidden="true" />
                            </span>
                          ) : null}
                          {tenant.company_name}
                        </strong>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <small
                            title={userEmail !== '-' ? userEmail : undefined}
                            style={{
                              display: 'block',
                              color: '#666',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '220px',
                            }}
                          >
                            <span className="tenant-inline-label">
                              <User size={13} aria-hidden="true" />
                              {userEmail}
                            </span>
                          </small>
                          <button
                            type="button"
                            onClick={() => openContactCard(tenant, userEmail, businessEmail, phoneNumber)}
                            title="biglietto da visita"
                            aria-label={`Apri biglietto da visita per ${tenant.company_name}`}
                            style={{
                              border: '1px solid #e5e7eb',
                              background: '#ffffff',
                              color: '#334155',
                              borderRadius: '999px',
                              fontSize: 13,
                              fontWeight: 700,
                              width: 26,
                              height: 26,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              lineHeight: 1,
                              padding: 0,
                            }}
                          >
                            <Contact size={14} aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span
                        title={formatDateTime(tenant.createdAt)}
                        style={{ display: 'block', whiteSpace: 'nowrap', fontSize: 12, color: '#374151' }}
                      >
                        {formatDate(tenant.createdAt)}
                      </span>
                      <small style={{ display: 'block', marginTop: 2, whiteSpace: 'nowrap', color: '#6b7280' }}>
                        {tenant.createdAt
                          ? new Date(tenant.createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
                          : '—'}
                      </small>
                    </td>
                    <td className="tenant-status-cell">
                      {tenant.status === 'SUSPENDED' && tenant.suspensionReason ? (
                        <span
                          title={`${tenant.suspensionReason === 'PAYMENT_FAILED' ? 'Pagamento fallito' : 'Sospeso manualmente'}${tenant.suspendedAt ? ` – ${new Date(tenant.suspendedAt).toLocaleDateString('it-IT')}` : ''}${tenant.suspensionNote ? `\n${tenant.suspensionNote}` : ''}`}
                          style={{ cursor: 'help', display: 'block', maxWidth: '100%' }}
                        >
                          {getStatusBadge(tenant.status)}
                        </span>
                      ) : getStatusBadge(tenant.status)}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          {tenant.planName
                            ? <span style={{ fontSize: 11, fontWeight: 600, background: '#f3e8ff', color: '#7c3aed', borderRadius: 6, padding: '2px 7px' }}>{tenant.planName}{tenant.planVersionNumber != null ? <span style={{ fontWeight: 400, opacity: 0.7 }}> (v{tenant.planVersionNumber})</span> : null}</span>
                            : <span style={{ fontSize: 11, color: '#9ca3af' }}>—</span>
                          }
                          {tenant.signedContractOffer?.commercialOfferId && (
                            <span
                              title={`Offerta commerciale: ${tenant.signedContractOffer.commercialOfferLabel}`}
                              style={{ fontSize: 11, fontWeight: 600, background: '#fce7f3', color: '#be185d', borderRadius: 4, padding: '2px 6px' }}
                            >
                              Offerta
                            </span>
                          )}
                        </div>
                        {tenant.subscriptionStatus && (
                          <span style={{
                            fontSize: 11, fontWeight: 600, borderRadius: 6, padding: '2px 7px', alignSelf: 'flex-start',
                            background: tenant.subscriptionStatus === 'ACTIVE' ? '#d1fae5' : tenant.subscriptionStatus === 'PAYMENT_PROCESSING' ? '#dbeafe' : tenant.subscriptionStatus === 'PAST_DUE' ? '#fef3c7' : tenant.subscriptionStatus === 'UNPAID' ? '#fee2e2' : '#f1f5f9',
                            color: tenant.subscriptionStatus === 'ACTIVE' ? '#065f46' : tenant.subscriptionStatus === 'PAYMENT_PROCESSING' ? '#1d4ed8' : tenant.subscriptionStatus === 'PAST_DUE' ? '#92400e' : tenant.subscriptionStatus === 'UNPAID' ? '#991b1b' : '#475569',
                          }}>{tenant.subscriptionStatus === 'PAYMENT_PROCESSING' ? 'Pagamento in elaborazione' : tenant.subscriptionStatus}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => openTrackerDrawer(tenant.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openTrackerDrawer(tenant.id);
                          }
                        }}
                        title={activation?.mainBlocker || 'Apri tracker attivazione'}
                        aria-label={`Apri tracker attivazione per ${tenant.company_name}`}
                        style={{
                          cursor: 'pointer',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          padding: '5px 7px',
                          background: '#fff',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8 }}>
                          {getActivationStatusBadge(activation?.activationStatus || 'UNKNOWN')}
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#374151' }}>{progressPercent}%</span>
                        </div>
                        <div style={{ width: '100%', height: 6, borderRadius: 999, background: '#e5e7eb', overflow: 'hidden' }}>
                          <div
                            style={{
                              width: `${progressPercent}%`,
                              height: '100%',
                              background:
                                normalizedActivationStatus === 'suspended_closed' || normalizedActivationStatus === 'blocked'
                                  ? '#dc3545'
                                  : normalizedActivationStatus === 'live' || normalizedActivationStatus === 'ready_for_go_live'
                                    ? '#198754'
                                    : '#fd7e14',
                            }}
                          />
                        </div>
                        <div style={{ marginTop: 6, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flexWrap: 'wrap' }}>
                            {isOperationallyBlocked && (
                              <span
                                title="Presente blocco operativo"
                                style={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  borderRadius: 999,
                                  padding: '2px 7px',
                                  background: '#fee2e2',
                                  color: '#b91c1c',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Bloccante
                              </span>
                            )}

                            {progressPercent < 100 && Number.isFinite(activation?.daysInCurrentStep) && (
                              <span style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' }}>
                                Fermo da {activation.daysInCurrentStep}g
                              </span>
                            )}
                          </div>

                          {normalizedActivationStatus === 'live'
                            ? (
                              activation?.lastTipReceivedAmount !== null
                              && activation?.lastTipReceivedAmount !== undefined
                              ? (
                                <span
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: '#166534',
                                    whiteSpace: 'nowrap',
                                  }}
                                  title={activation?.lastTipReceivedAt ? `Ultima mancia: ${formatDateTime(activation.lastTipReceivedAt)}` : 'Ultima mancia ricevuta'}
                                >
                                  Ultima mancia: {formatCurrencyAmount(activation?.lastTipReceivedAmount, activation?.lastTipReceivedCurrency || 'EUR')}
                                </span>
                              )
                              : null
                            )
                            : (
                              <span
                                aria-label="Info reminder"
                                onMouseEnter={() => setHoveredReminderTooltipTenantId(tenant.id)}
                                onMouseLeave={() => setHoveredReminderTooltipTenantId((prev) => (prev === tenant.id ? null : prev))}
                                onFocus={() => setHoveredReminderTooltipTenantId(tenant.id)}
                                onBlur={() => setHoveredReminderTooltipTenantId((prev) => (prev === tenant.id ? null : prev))}
                                onClick={(event) => event.stopPropagation()}
                                style={{
                                  position: 'relative',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 14,
                                  lineHeight: 1,
                                  color: activation?.lastReminderAt ? '#334155' : '#94a3b8',
                                  userSelect: 'none',
                                }}
                              >
                                <Bell size={14} aria-hidden="true" />
                                {hoveredReminderTooltipTenantId === tenant.id && (
                                  <span
                                    role="tooltip"
                                    style={{
                                      position: 'absolute',
                                      right: 0,
                                      bottom: 'calc(100% + 10px)',
                                      minWidth: '220px',
                                      maxWidth: '280px',
                                      background: '#0f172a',
                                      color: '#f8fafc',
                                      borderRadius: '8px',
                                      padding: '10px 12px',
                                      fontSize: '12px',
                                      fontWeight: 500,
                                      lineHeight: 1.35,
                                      boxShadow: '0 10px 24px rgba(15, 23, 42, 0.35)',
                                      zIndex: 150,
                                      textAlign: 'left',
                                    }}
                                  >
                                    <span style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#93c5fd', marginBottom: 6 }}>
                                      Ultimo Reminder
                                    </span>
                                    <span style={{ display: 'block' }}>
                                      Quando: {activation?.lastReminderAt ? formatDateTime(activation.lastReminderAt) : 'Nessun reminder inviato'}
                                    </span>
                                    <span style={{ display: 'block' }}>
                                      Relativo: {activation?.lastReminderAt ? formatRelativeTimeCompact(activation.lastReminderAt) : 'mai'}
                                    </span>
                                    <span style={{ display: 'block' }}>
                                      Tipo: {getReminderTypeLabel(activation?.lastReminderType)}
                                    </span>
                                    <span style={{ display: 'block' }}>
                                      Da: {activation?.lastReminderBy || 'N/D'}
                                    </span>
                                    <span
                                      aria-hidden="true"
                                      style={{
                                        position: 'absolute',
                                        right: '10px',
                                        top: '100%',
                                        width: 0,
                                        height: 0,
                                        borderLeft: '6px solid transparent',
                                        borderRight: '6px solid transparent',
                                        borderTop: '6px solid #0f172a',
                                      }}
                                    />
                                  </span>
                                )}
                              </span>
                            )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                          gap: '3px',
                          minWidth: 0,
                          maxWidth: 88,
                        }}
                      >
                        <span
                          title="Numero locali (venues)"
                          aria-label={`Locali: ${stats.venues}`}
                          style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '1px 4px', background: '#f8fafc', fontSize: 10, color: '#334155', whiteSpace: 'nowrap', textAlign: 'center' }}
                        >
                          <span className="tenant-structure-stat">
                            <Store size={11} aria-hidden="true" />
                            <strong style={{ color: '#0f172a' }}>{stats.venues}</strong>
                          </span>
                        </span>
                        <span
                          title="Numero team"
                          aria-label={`Team: ${stats.teams}`}
                          style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '1px 4px', background: '#f8fafc', fontSize: 10, color: '#334155', whiteSpace: 'nowrap', textAlign: 'center' }}
                        >
                          <span className="tenant-structure-stat">
                            <Users size={11} aria-hidden="true" />
                            <strong style={{ color: '#0f172a' }}>{stats.teams}</strong>
                          </span>
                        </span>
                        <span
                          title="Numero percettori/worker"
                          aria-label={`Percettori: ${stats.workers}`}
                          style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '1px 4px', background: '#f8fafc', fontSize: 10, color: '#334155', whiteSpace: 'nowrap', textAlign: 'center' }}
                        >
                          <span className="tenant-structure-stat">
                            <User size={11} aria-hidden="true" />
                            <strong style={{ color: '#0f172a' }}>{stats.workers}</strong>
                          </span>
                        </span>
                        <span
                          title="Numero totem/device"
                          aria-label={`Totem: ${stats.devices}`}
                          style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '1px 4px', background: '#f8fafc', fontSize: 10, color: '#334155', whiteSpace: 'nowrap', textAlign: 'center' }}
                        >
                          <span className="tenant-structure-stat">
                            <Smartphone size={11} aria-hidden="true" />
                            <strong style={{ color: '#0f172a' }}>{stats.devices}</strong>
                          </span>
                        </span>
                      </div>
                    </td>
                    <td>
                      <RowActions
                        tenant={tenant}
                        onActivate={() => openActivateModal(tenant)}
                        onSuspend={() => openStatusChangeModal(tenant, 'SUSPENDED')}
                        onResume={() => openStatusChangeModal(tenant, 'ACTIVE')}
                        onDelete={() => handleDeleteTenant(tenant.id)}
                        onLegalDocuments={() => openLegalDocumentsModal(tenant)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredAndSortedTenants.length === 0 && !loading && (
          <p style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            {searchQuery.trim() || filters.status || filters.hasTotem || filters.minLocals || filters.minWorkers || filters.hideSuspended === false || activationFilter
              ? `Nessun tenant trovato con i filtri applicati.`
              : 'Nessun tenant trovato nel sistema.'
            }
          </p>
        )}

        {filteredAndSortedTenants.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(filteredAndSortedTenants.length / itemsPerPage)}
            totalItems={filteredAndSortedTenants.length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        )}
      </div>

      {/* Modale Conferma Cambio Status */}
      {contractReminderPreviewModal.open && (
        <div
          className="modal-overlay"
          onClick={closeContractReminderPreview}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '760px',
              width: '94%',
              maxHeight: '88vh',
              overflowY: 'auto',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            }}
          >
            <div style={{ marginBottom: '14px' }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#0f172a' }}>
                <span className="tenant-inline-label">
                  <Mail size={18} aria-hidden="true" />
                  Anteprima email reminder
                </span>
              </h3>
              <p style={{ margin: 0, color: '#475569', fontSize: '13px' }}>
                Verifica contenuto e destinatario prima dell&apos;invio.
              </p>
            </div>

            {contractReminderPreviewModal.loading && (
              <div style={{ color: '#64748b', fontSize: '14px' }}>Caricamento anteprima in corso...</div>
            )}

            {!contractReminderPreviewModal.loading && contractReminderPreviewModal.data && (
              <div style={{ display: 'grid', gap: '10px' }}>
                <div
                  style={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    background: '#f8fafc',
                    padding: '12px',
                  }}
                >
                  <div style={{ color: '#334155', fontSize: '12px', fontWeight: 700, marginBottom: '8px' }}>TO</div>
                  <div style={{ marginBottom: '8px', color: '#0f172a', fontSize: '13px' }}>
                    <strong>Destinatario principale:</strong> {contractReminderPreviewModal.data.recipient}
                  </div>
                  <textarea
                    value={contractReminderPreviewModal.additionalRecipients}
                    onChange={(e) => setContractReminderPreviewModal((prev) => ({ ...prev, additionalRecipients: e.target.value }))}
                    placeholder="Aggiungi altri destinatari (separati da virgola, punto e virgola o invio)"
                    style={{
                      width: '100%',
                      minHeight: '70px',
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      color: '#0f172a',
                      fontSize: '12px',
                      lineHeight: 1.45,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      padding: '10px',
                      background: '#fff',
                    }}
                  />
                </div>
                <div
                  style={{
                    border: '1px solid #dbeafe',
                    borderRadius: '8px',
                    background: '#eff6ff',
                    padding: '12px',
                  }}
                >
                  <div style={{ color: '#1e3a8a', fontSize: '12px', fontWeight: 700, marginBottom: '8px' }}>
                    Anteprima HTML
                  </div>
                  <div
                    style={{
                      border: '1px solid #bfdbfe',
                      borderRadius: '6px',
                      background: '#ffffff',
                      padding: '12px',
                      maxHeight: '360px',
                      overflowY: 'auto',
                    }}
                    dangerouslySetInnerHTML={{ __html: contractReminderPreviewModal.data.html || '' }}
                  />
                </div>
              </div>
            )}

            {contractReminderPreviewModal.error && (
              <div style={{ marginTop: '12px', color: '#b91c1c', fontSize: '13px' }}>
                {contractReminderPreviewModal.error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button
                onClick={closeContractReminderPreview}
                style={{
                  padding: '10px 18px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                Annulla
              </button>
              <button
                onClick={handleSendContractReminderFromPreview}
                disabled={contractReminderPreviewModal.loading || contractReminderPreviewModal.sending || !contractReminderPreviewModal.data}
                style={{
                  padding: '10px 18px',
                  border: 'none',
                  background: '#1d4ed8',
                  color: 'white',
                  borderRadius: '6px',
                  cursor: (contractReminderPreviewModal.loading || contractReminderPreviewModal.sending || !contractReminderPreviewModal.data) ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: 700,
                  opacity: (contractReminderPreviewModal.loading || contractReminderPreviewModal.sending || !contractReminderPreviewModal.data) ? 0.65 : 1,
                }}
              >
                {contractReminderPreviewModal.sending ? 'Invio in corso...' : 'Invia email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale Conferma Cambio Status */}
      {statusChangeModal.open && statusChangeModal.tenant && (
        <div
          className="modal-overlay"
          onClick={closeStatusChangeModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
            }}
          >
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 16px 0', color: statusChangeModal.newStatus === 'SUSPENDED' ? '#ffc107' : '#28a745' }}>
                <span className="tenant-inline-label">
                  {statusChangeModal.newStatus === 'SUSPENDED'
                    ? <AlertTriangle size={18} aria-hidden="true" />
                    : <CheckCircle2 size={18} aria-hidden="true" />}
                  {statusChangeModal.newStatus === 'SUSPENDED' ? 'Sospendi tenant' : 'Riattiva tenant'}
                </span>
              </h3>
              <p style={{ margin: '0 0 12px 0', color: '#666' }}>
                Confermi di voler {statusChangeModal.newStatus === 'SUSPENDED' ? 'sospendere' : 'riattivare'} il tenant:
              </p>
              <div
                style={{
                  background: '#f8f9fa',
                  padding: '16px',
                  borderRadius: '6px',
                  border: '1px solid #dee2e6'
                }}
              >
                <div style={{ marginBottom: '8px' }}>
                  <strong className="tenant-inline-label"><Building2 size={14} aria-hidden="true" /> Azienda:</strong> {statusChangeModal.tenant.company_name}
                </div>
                <div>
                  <strong className="tenant-inline-label"><Mail size={14} aria-hidden="true" /> Email:</strong> {statusChangeModal.tenant.email}
                </div>
              </div>
              {statusChangeModal.newStatus === 'SUSPENDED' && (
                <div style={{ marginTop: '16px' }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '6px', fontSize: '14px' }}>
                    <span className="tenant-inline-label">
                      <FileText size={14} aria-hidden="true" />
                      Motivo sospensione (visibile solo all&apos;admin)
                    </span>
                  </label>
                  <textarea
                    value={statusChangeModal.suspensionNote}
                    onChange={(e) => setStatusChangeModal(prev => ({ ...prev, suspensionNote: e.target.value }))}
                    placeholder="Es: mancato pagamento, violazione contratto..."
                    rows={3}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical', boxSizing: 'border-box' }}
                  />
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={closeStatusChangeModal}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ddd',
                  background: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                <span className="tenant-inline-label"><X size={15} aria-hidden="true" /> Annulla</span>
              </button>
              <button
                onClick={handleChangeStatus}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  background: statusChangeModal.newStatus === 'SUSPENDED' ? '#ffc107' : '#28a745',
                  color: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                <span className="tenant-inline-label">
                  {statusChangeModal.newStatus === 'SUSPENDED'
                    ? <AlertTriangle size={15} aria-hidden="true" />
                    : <CheckCircle2 size={15} aria-hidden="true" />}
                  {statusChangeModal.newStatus === 'SUSPENDED' ? 'Conferma sospensione' : 'Conferma riattivazione'}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale Attivazione Tenant */}
      {activateModalOpen && activatingTenant && (
        <div
          className="modal-overlay"
          onClick={closeActivateModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
            }}
          >
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#28a745' }}>
                <span className="tenant-inline-label">
                  <CheckCircle2 size={18} aria-hidden="true" />
                  Attiva tenant
                </span>
              </h3>
              <p style={{ margin: '0 0 12px 0', color: '#666' }}>
                Stai per attivare il tenant:
              </p>
              <div
                style={{
                  background: '#f8f9fa',
                  padding: '16px',
                  borderRadius: '6px',
                  border: '1px solid #dee2e6'
                }}
              >
                <div style={{ marginBottom: '8px' }}>
                  <strong className="tenant-inline-label"><Building2 size={14} aria-hidden="true" /> Azienda:</strong> {activatingTenant.company_name}
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <strong className="tenant-inline-label"><Mail size={14} aria-hidden="true" /> Email:</strong> {activatingTenant.email}
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <strong className="tenant-inline-label"><BarChart3 size={14} aria-hidden="true" /> Status attuale:</strong> {getStatusBadge(activatingTenant.status)}
                </div>
                {activatingTenant.signedContractOffer && (
                  <CommercialOfferBox
                    label={activatingTenant.signedContractOffer.commercialOfferLabel}
                    feePercent={activatingTenant.signedContractOffer.commercialOfferFeePercent}
                    feeFixedCents={activatingTenant.signedContractOffer.commercialOfferFeeFixedCents}
                    trialDays={activatingTenant.signedContractOffer.commercialOfferTrialDays}
                    setupFeeCents={activatingTenant.signedContractOffer.commercialOfferSetupFeeCents}
                    softwareSubscriptionFeeCents={activatingTenant.signedContractOffer.commercialOfferSoftwareSubscriptionFeeCents}
                    notes={activatingTenant.signedContractOffer.commercialOfferNotes}
                    style={{ marginTop: '10px' }}
                  />
                )}
              </div>
              <div
                style={{
                  marginTop: '16px',
                  padding: '12px',
                  background: '#d4edda',
                  border: '1px solid #c3e6cb',
                  borderRadius: '4px',
                  color: '#155724'
                }}
              >
                <strong className="tenant-inline-label"><AlertTriangle size={14} aria-hidden="true" /> Attenzione:</strong> L'attivazione del tenant permetterà l'accesso completo alla piattaforma e l'utilizzo di tutti i servizi.
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={closeActivateModal}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ddd',
                  background: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                <span className="tenant-inline-label"><X size={15} aria-hidden="true" /> Annulla</span>
              </button>
              <button
                onClick={handleActivateTenant}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  background: '#28a745',
                  color: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                <span className="tenant-inline-label"><CheckCircle2 size={15} aria-hidden="true" /> Conferma attivazione</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <TenantLegalDocumentsModal
        open={legalDocumentsModal.open}
        tenantId={legalDocumentsModal.tenantId}
        onClose={closeLegalDocumentsModal}
      />

      {trackerDrawerOpen && (
        <>
          <div className="at-drawer-overlay" onClick={closeTrackerDrawer} />
          <aside className="at-drawer" role="dialog" aria-modal="true" aria-labelledby="activation-tracker-drawer-title">
            <div className="at-drawer-scroll">
              {trackerDetailLoading ? (
                <>
                  <div className="at-drawer-header">
                    <h2 id="activation-tracker-drawer-title">Tracker attivazione</h2>
                    <button className="at-drawer-close" type="button" onClick={closeTrackerDrawer} aria-label="Chiudi tracker">
                      <X size={18} />
                    </button>
                  </div>
                  <div className="at-detail-empty">Caricamento dettaglio...</div>
                </>
              ) : trackerDetailError ? (
                <>
                  <div className="at-drawer-header">
                    <h2 id="activation-tracker-drawer-title">Tracker attivazione</h2>
                    <button className="at-drawer-close" type="button" onClick={closeTrackerDrawer} aria-label="Chiudi tracker">
                      <X size={18} />
                    </button>
                  </div>
                  <div className="at-detail-error">{trackerDetailError}</div>
                </>
              ) : trackerDetail ? (
                <>
                  <div className="at-detail-header">
                    <div>
                      <h2 id="activation-tracker-drawer-title">{trackerDetail.tenantName}</h2>
                      <div className="at-detail-meta">
                        <span>{trackerDetail.tenantStatus}</span>
                        {getActivationStatusBadge(trackerDetail.activationStatus)}
                        <span>{trackerDetail.progressPercent}% completato</span>
                      </div>
                    </div>

                    <button className="at-drawer-close" type="button" onClick={closeTrackerDrawer} aria-label="Chiudi tracker">
                      <X size={18} />
                    </button>
                  </div>

                  {(trackerDetail.mainBlocker || trackerDetail.nextActionLabel) && (
                    <div className={`at-ops-card at-ops-card--${trackerDetail.blockerType || trackerDetail.blockerSeverity || 'medium'}`}>
                      <div className="at-ops-card__header">
                        <div className="at-ops-card__title">
                          <AlertTriangle size={16} />
                          <strong>Quadro operativo</strong>
                        </div>
                        <SignalBadge type={trackerDetail.blockerType} />
                      </div>

                      <div className="at-ops-card__grid">
                        <div>
                          <span className="at-ops-card__label">Stato</span>
                          <span className="at-ops-card__value">
                            {trackerDetail.isOperationallyBlocked ? 'Bloccante operativo' : 'Nessun blocco operativo'}
                          </span>
                        </div>
                        <div>
                          <span className="at-ops-card__label">Step corrente</span>
                          <span className="at-ops-card__value">{trackerDetail.currentStep || 'Live'}</span>
                        </div>
                        <div>
                          <span className="at-ops-card__label">Tempo nello step</span>
                          <span className="at-ops-card__value">{formatDays(trackerDetail.daysInCurrentStep, trackerDetail.daysInCurrentStepLabel)}</span>
                        </div>
                        <div>
                          <span className="at-ops-card__label">Prossima azione</span>
                          <span className="at-ops-card__value">{trackerDetail.nextActionLabel || 'Nessuna azione richiesta'}</span>
                        </div>
                      </div>

                      {trackerDetail.mainBlocker && (
                        <div className="at-ops-card__blocker">
                          ⚠️ {trackerDetail.mainBlocker}
                        </div>
                      )}

                    </div>
                  )}

                  <div className="at-blocker at-blocker--info">
                    <Info size={18} />
                    <div>
                      <div className="at-blocker-title-row">
                        <strong>Reminder tracking</strong>
                      </div>
                      <span title={trackerDetail.reminderTracking?.lastManual?.sentAt ? formatDateTime(trackerDetail.reminderTracking.lastManual.sentAt) : 'N/D'}>
                        Ultimo manuale: {trackerDetail.reminderTracking?.lastManual
                          ? `${formatRelativeTime(trackerDetail.reminderTracking.lastManual.sentAt)} - ${trackerDetail.reminderTracking.lastManual.initiatedBy?.email || trackerDetail.reminderTracking.lastManual.initiatedBy?.displayName || 'N/D'}`
                          : 'mai inviato'}
                      </span>
                      <span title={trackerDetail.reminderTracking?.lastAutomatic?.sentAt ? formatDateTime(trackerDetail.reminderTracking.lastAutomatic.sentAt) : 'N/D'}>
                        Ultimo automatico: {trackerDetail.reminderTracking?.lastAutomatic
                          ? `${formatRelativeTime(trackerDetail.reminderTracking.lastAutomatic.sentAt)} - ${trackerDetail.reminderTracking.lastAutomatic.initiatedBy?.source || 'scheduler'}`
                          : 'mai inviato'}
                      </span>
                      <span title={trackerDetail.reminderTracking?.lastAny?.sentAt ? formatDateTime(trackerDetail.reminderTracking.lastAny.sentAt) : 'N/D'}>
                        Ultimo invio assoluto: {trackerDetail.reminderTracking?.lastAny
                          ? `${formatRelativeTime(trackerDetail.reminderTracking.lastAny.sentAt)} (${(trackerDetail.reminderTracking.lastAny.triggerType || '').toLowerCase()})`
                          : 'nessun invio'}
                      </span>
                    </div>
                  </div>

                  <div className="at-timeline">
                    {Array.isArray(trackerDetail.activationSections) && trackerDetail.activationSections.length > 0 ? (
                      <div className="at-sections">
                        {trackerDetail.activationSections.map((section) => (
                          <section className={`at-section-card ${section.key === 'venue_registration' ? 'at-section-card--venue-registration' : ''}`} key={section.key}>
                            <div className="at-section-header">
                              <h3>{section.label}</h3>
                              {section.description && <p>{section.description}</p>}
                            </div>

                            <div className="at-section-steps">
                              {(section.steps || []).map((step, stepIndex) => (
                                <div className={`at-section-step at-section-step--${step.status}`} key={step.key || `${section.key}-${stepIndex}`}>
                                  <div className="at-section-step-marker">
                                    <div className="at-step-icon">
                                      <StepIcon status={step.status} />
                                    </div>
                                    <span className={`at-section-step-owner ${(step.owner || '').toLowerCase() === 'soontip' ? 'at-section-step-owner--soontip' : ''}`}>
                                      {(step.ownerTag || step.ownerLabel || step.owner || '').toString().toLowerCase()}
                                    </span>
                                  </div>

                                  <div className="at-section-step-body">
                                    <div className="at-step-title-row">
                                      <h3>{step.label}</h3>
                                      <div className="at-step-badges">
                                        <span>{STEP_STATUS_LABELS[step.status] || step.status}</span>
                                      </div>
                                    </div>

                                    {step.description && <p className="at-section-step-description">{step.description}</p>}
                                    {step.detail && (
                                      <p className="at-section-step-description">
                                        Piano selezionato: <span style={{ display: 'inline-block', padding: '1px 9px', borderRadius: 12, background: '#f3e8ff', color: '#7c3aed', fontWeight: 700, fontSize: 13 }}>{step.detail}</span>
                                      </p>
                                    )}

                                    <div className="at-step-meta">
                                      <span className={(step.owner || '').toLowerCase() === 'soontip' ? 'at-owner-label at-owner-label--soontip' : 'at-owner-label'}>
                                        Responsabile: {step.ownerLabel || step.owner}
                                      </span>
                                      {step.completedAt && <span>Completato: {formatDate(step.completedAt)}</span>}
                                    </div>

                                    {step.blockingReason && step.status !== 'completed' && (
                                      <p className="at-step-blocking">{step.blockingReason}</p>
                                    )}

                                    {step.isActionAvailable && step.actionType && (
                                      (() => {
                                        const currentStep = String(trackerDetail?.currentStep || '');
                                        const isBlockedOnTenantStep = currentStep.startsWith('Registrazione tenant:');
                                        const shouldHideVenueActions = isBlockedOnTenantStep && section.key === 'venue_registration';
                                        
                                        if (shouldHideVenueActions) return null;
                                        
                                        return (
                                          <div className="at-section-step-actions">
                                            <button
                                              type="button"
                                              className="at-section-step-action"
                                              onClick={() => handleSectionStepAction(section, step)}
                                              disabled={sectionActionLoadingKey === `${section.key}:${step.key}:${trackerTenantId}`}
                                            >
                                              {sectionActionLoadingKey === `${section.key}:${step.key}:${trackerTenantId}`
                                                ? 'Invio in corso...'
                                                : (step.actionLabel || 'Esegui azione')}
                                            </button>
                                          </div>
                                        );
                                      })()
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>

                            {section.key === 'tenant_registration' && trackerDetail.onboardingCommercialOffer && (
                              <div className="at-offer-summary">
                                <div className="at-offer-summary__title">Offerta commerciale in attivazione</div>
                                <div className="at-offer-summary__grid">
                                  <div>
                                    <span>Nome</span>
                                    <strong>{trackerDetail.onboardingCommercialOffer.label || 'Offerta standard'}</strong>
                                  </div>
                                  <div>
                                    <span>Status</span>
                                    <strong>{trackerDetail.onboardingCommercialOffer.status || 'N/D'}</strong>
                                  </div>
                                  <div>
                                    <span>Fee %</span>
                                    <strong>{trackerDetail.onboardingCommercialOffer.feePercent ?? 0}%</strong>
                                  </div>
                                  <div>
                                    <span>Fee fissa</span>
                                    <strong>{formatCurrencyCents(trackerDetail.onboardingCommercialOffer.feeFixedCents)}</strong>
                                  </div>
                                  <div>
                                    <span>Trial</span>
                                    <strong>{trackerDetail.onboardingCommercialOffer.trialDays ?? 0} giorni</strong>
                                  </div>
                                  <div>
                                    <span>Setup fee</span>
                                    <strong>{formatCurrencyCents(trackerDetail.onboardingCommercialOffer.setupFeeCents)}</strong>
                                  </div>
                                  <div>
                                    <span>Canone software</span>
                                    <strong>{formatCurrencyCents(trackerDetail.onboardingCommercialOffer.softwareSubscriptionFeeCents)}</strong>
                                  </div>
                                </div>
                                {trackerDetail.onboardingCommercialOffer.notes && (
                                  <p className="at-offer-summary__notes">{trackerDetail.onboardingCommercialOffer.notes}</p>
                                )}
                              </div>
                            )}

                            {Array.isArray(section.venues) && section.venues.length > 0 && (
                              <div className="at-venues-list">
                                {section.venues.map((venue) => {
                                  const deviceOwnerIsSoontip = (venue.deviceCheck?.owner || '').toLowerCase() === 'soontip';
                                  const collectionOwnerIsSoontip = (venue.collectionCheck?.owner || '').toLowerCase() === 'soontip';
                                  const kioskChannel = venue.collectionCheck?.channels?.kiosk || {};
                                  const tipLinksChannel = venue.collectionCheck?.channels?.tipLinks || {};
                                  const deviceCapabilityEnabled = kioskChannel.enabled === true;
                                  const visibleDevicesCount = Array.isArray(venue.devices) ? venue.devices.length : 0;
                                  const disabledDevicesCount = Number(venue.deviceCheck?.disabledDevicesCount || 0);
                                  const currentStep = String(trackerDetail?.currentStep || '');
                                  const isBlockedOnTenantStep = currentStep.startsWith('Registrazione tenant:');
                                  const venueAllDevicesDisabled = Boolean(
                                    venue.deviceCheck?.allDevicesDisabled
                                    || (visibleDevicesCount === 0 && disabledDevicesCount > 0)
                                  );
                                  return (
                                    <article className="at-venue-card" key={venue.venueId || venue.venueName}>
                                      <div className="at-venue-card__header">
                                        <h4>{venue.venueName}</h4>
                                        <span className="at-venue-card__devices-count">
                                          Kiosk: {kioskChannel.enabled ? `${kioskChannel.activeCount || 0}/${visibleDevicesCount} attivi` : 'non incluso'}
                                          {' · '}
                                          QR: {tipLinksChannel.enabled ? `${tipLinksChannel.activeCount || 0} attivi` : 'non incluso'}
                                        </span>
                                      </div>

                                      <div className="at-venue-card__block">
                                        <div className="at-venue-card__block-title">Canali di incasso</div>
                                        <div className="at-venue-check-row">
                                          <div className="at-venue-check-marker">
                                            <span className="at-step-icon"><StepIcon status={venue.collectionCheck?.status} /></span>
                                            <span className={`at-section-step-owner ${collectionOwnerIsSoontip ? 'at-section-step-owner--soontip' : ''}`}>
                                              {String(venue.collectionCheck?.ownerLabel || 'Cliente').toLowerCase()}
                                            </span>
                                          </div>
                                          <div>
                                            <strong>Almeno un canale previsto dal piano attivo</strong>
                                            <div className="at-step-meta">
                                              <span className="at-owner-label">
                                                Kiosk: {!kioskChannel.enabled
                                                  ? 'non incluso'
                                                  : (kioskChannel.ready ? `${kioskChannel.activeCount} attivi` : 'da attivare')}
                                              </span>
                                              <span className="at-owner-label">
                                                QR: {!tipLinksChannel.enabled
                                                  ? 'non incluso'
                                                  : (tipLinksChannel.ready ? `${tipLinksChannel.activeCount} attivi` : 'da creare o attivare')}
                                              </span>
                                            </div>
                                            {venue.collectionCheck?.blockingReason && venue.collectionCheck?.status !== 'completed' && (
                                              <p className="at-step-blocking">{venue.collectionCheck.blockingReason}</p>
                                            )}
                                          </div>
                                        </div>
                                      </div>

                                      <div className="at-venue-card__block">
                                        <div className="at-venue-card__block-title">
                                          Device (esclusi disabilitati) {!deviceCapabilityEnabled && '— non inclusi nel piano'}
                                        </div>
                                        <div className="at-venue-check-row">
                                          <div className="at-venue-check-marker">
                                            <span className="at-step-icon"><StepIcon status={venue.deviceCheck?.status} /></span>
                                            <span className={`at-section-step-owner ${deviceOwnerIsSoontip ? 'at-section-step-owner--soontip' : ''}`}>
                                              {String(venue.deviceCheck?.ownerTag || venue.deviceCheck?.owner || venue.deviceCheck?.ownerLabel || 'cliente').toLowerCase()}
                                            </span>
                                          </div>
                                          <div>
                                            <strong>
                                              {!deviceCapabilityEnabled
                                                ? 'Canale kiosk non abilitato dal piano'
                                                : venueAllDevicesDisabled
                                                ? 'Locale in pausa operativa'
                                                : 'Tutti i device assegnati attivi'}
                                            </strong>
                                            <div className="at-step-meta">
                                              <span className={deviceOwnerIsSoontip ? 'at-owner-label at-owner-label--soontip' : 'at-owner-label'}>
                                                Responsabile: {venue.deviceCheck?.ownerLabel || venue.deviceCheck?.owner || 'N/D'}
                                              </span>
                                            </div>
                                            {venueAllDevicesDisabled && (
                                              <p className="at-step-info">Locale in pausa operativa: tutti i device del locale sono disabilitati da SoonTip.</p>
                                            )}
                                            {venue.deviceCheck?.blockingReason && venue.deviceCheck?.status !== 'completed' && (
                                              <p className="at-step-blocking">{venue.deviceCheck.blockingReason}</p>
                                            )}
                                          </div>
                                        </div>

                                        {Array.isArray(venue.devices) && venue.devices.length > 0 ? (
                                          <div className="at-venue-device-list at-venue-device-list--detailed">
                                            {venue.devices.map((device) => {
                                              const deviceStatus = String(device.status || '').toUpperCase();
                                              const deviceOwnerIsSoontip = deviceStatus === 'PROVISIONED';
                                              const deviceOwnerLabel = deviceOwnerIsSoontip ? 'SoonTip' : 'Cliente';
                                              const deviceActionLabel = !deviceCapabilityEnabled
                                                ? null
                                                : deviceStatus === 'PROVISIONED'
                                                ? 'Imposta status: Spedito'
                                                : (deviceStatus === 'SHIPPED_PENDING_ACTIVATION' ? 'Invia reminder attivazione' : null);
                                              const deviceActionLoadingKey = `venue:${venue?.venueId || venue?.venueName || 'unknown'}:device:${device.id}:${trackerTenantId}`;

                                              return (
                                                <div className="at-venue-device-item at-venue-device-item--detailed" key={device.id || device.setupCode || device.label}>
                                                  <div className="at-venue-device-item__layout">
                                                    <div className="at-venue-check-marker">
                                                      <span className="at-step-icon"><StepIcon status={device.status === 'ACTIVE' ? 'completed' : 'in_progress'} /></span>
                                                      <span className={`at-section-step-owner ${deviceOwnerIsSoontip ? 'at-section-step-owner--soontip' : ''}`}>
                                                        {String(deviceOwnerLabel || 'Cliente').toLowerCase()}
                                                      </span>
                                                    </div>

                                                    <div className="at-venue-device-item__content">
                                                      <div className="at-venue-device-item__head">
                                                        <span className="at-venue-device-name">{device.label || device.setupCode || 'Device'}</span>
                                                        <span
                                                          className={`at-venue-device-status at-venue-device-status--${String(device.status || 'unknown').toLowerCase()}`}
                                                        >
                                                          {DEVICE_STATUS_LABELS[device.status] || device.statusLabel || device.status || 'N/D'}
                                                        </span>
                                                      </div>

                                                      <div className="at-step-meta">
                                                        <span className={deviceOwnerIsSoontip ? 'at-owner-label at-owner-label--soontip' : 'at-owner-label'}>
                                                          Responsabile: {deviceOwnerLabel}
                                                        </span>
                                                        {device.setupCode && <span>Setup code: {device.setupCode}</span>}
                                                      </div>

                                                      {deviceStatus === 'PROVISIONED' && (
                                                        <p className="at-step-blocking">Device provisionato: SoonTip deve impostarlo in Shipped/Pending activation.</p>
                                                      )}
                                                      {deviceStatus === 'SHIPPED_PENDING_ACTIVATION' && (
                                                        <p className="at-step-blocking">Device spedito: il cliente deve completare attivazione accensione + codice ST-XXXXXX + wizard.</p>
                                                      )}
                                                      {deviceStatus === 'ACTIVE' && (
                                                        <p className="at-venue-empty">Device attivo. Nessuna azione richiesta.</p>
                                                      )}

                                                      {deviceActionLabel && !isBlockedOnTenantStep && (
                                                        <div className="at-section-step-actions">
                                                          <button
                                                            type="button"
                                                            className="at-section-step-action"
                                                            onClick={() => handleSingleVenueDeviceAction(venue, device)}
                                                            disabled={sectionActionLoadingKey === deviceActionLoadingKey}
                                                          >
                                                            {sectionActionLoadingKey === deviceActionLoadingKey
                                                              ? 'Operazione in corso...'
                                                              : deviceActionLabel}
                                                          </button>
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        ) : (
                                          <p className="at-venue-empty">Nessun device visibile per questo locale.</p>
                                        )}
                                      </div>

                                      <div className="at-venue-card__block">
                                        <div className="at-venue-card__block-title">Worker del locale</div>
                                        <div className="at-venue-check-row">
                                          <div className="at-venue-check-marker">
                                            <span className="at-step-icon"><StepIcon status={venue.workerChecks?.activeWorkers?.status} /></span>
                                            <span className="at-section-step-owner">
                                              {String(venue.workerChecks?.activeWorkers?.ownerTag || venue.workerChecks?.activeWorkers?.owner || venue.workerChecks?.activeWorkers?.ownerLabel || 'cliente').toLowerCase()}
                                            </span>
                                          </div>
                                          <div>
                                            <strong>Almeno un worker attivo presente ({venue.workerChecks?.activeWorkers?.count ?? 0})</strong>
                                            <div className="at-step-meta">
                                              <span className="at-owner-label">Responsabile: {venue.workerChecks?.activeWorkers?.ownerLabel || 'Cliente'}</span>
                                            </div>
                                            {venue.workerChecks?.activeWorkers?.blockingReason && venue.workerChecks?.activeWorkers?.status !== 'completed' && (
                                              <p className="at-step-blocking">{venue.workerChecks.activeWorkers.blockingReason}</p>
                                            )}
                                          </div>
                                        </div>

                                        <div className="at-venue-check-row">
                                          <div className="at-venue-check-marker">
                                            <span className="at-step-icon"><StepIcon status={venue.workerChecks?.connectedWorkers?.status} /></span>
                                            <span className="at-section-step-owner">
                                              {String(venue.workerChecks?.connectedWorkers?.ownerTag || venue.workerChecks?.connectedWorkers?.owner || venue.workerChecks?.connectedWorkers?.ownerLabel || 'cliente').toLowerCase()}
                                            </span>
                                          </div>
                                          <div>
                                            <strong>Almeno un worker connesso ({venue.workerChecks?.connectedWorkers?.count ?? 0})</strong>
                                            <div className="at-step-meta">
                                              <span className="at-owner-label">Responsabile: {venue.workerChecks?.connectedWorkers?.ownerLabel || 'Cliente'}</span>
                                            </div>
                                            {venue.workerChecks?.connectedWorkers?.blockingReason && venue.workerChecks?.connectedWorkers?.status !== 'completed' && (
                                              <p className="at-step-blocking">{venue.workerChecks.connectedWorkers.blockingReason}</p>
                                            )}
                                          </div>
                                        </div>

                                        {(() => {
                                          const workerReminderCheck = venue.workerChecks?.connectedWorkers?.isActionAvailable
                                            ? venue.workerChecks.connectedWorkers
                                            : (venue.workerChecks?.activeWorkers?.isActionAvailable
                                              ? venue.workerChecks.activeWorkers
                                              : null);

                                          if (!workerReminderCheck?.actionType || isBlockedOnTenantStep) return null;

                                          const workerReminderLoadingKey = `venue:${venue?.venueId || venue?.venueName || 'unknown'}:worker:${workerReminderCheck?.actionStepKey || 'default'}:${trackerTenantId}`;

                                          return (
                                            <div className="at-section-step-actions">
                                              <button
                                                type="button"
                                                className="at-section-step-action"
                                                onClick={() => handleWorkerCheckReminderAction(venue, workerReminderCheck)}
                                                disabled={sectionActionLoadingKey === workerReminderLoadingKey}
                                              >
                                                {sectionActionLoadingKey === workerReminderLoadingKey
                                                  ? 'Invio in corso...'
                                                  : (workerReminderCheck?.actionLabel || 'Invia reminder lavoratori')}
                                              </button>
                                            </div>
                                          );
                                        })()}
                                      </div>

                                      <div className="at-venue-card__block">
                                        <div className="at-venue-card__block-title">Mancia</div>
                                        <div className="at-venue-check-row">
                                          <div className="at-venue-check-marker">
                                            <span className="at-step-icon"><StepIcon status={venue.tipCheck?.status} /></span>
                                            <span className="at-section-step-owner">
                                              {String(venue.tipCheck?.ownerTag || venue.tipCheck?.owner || venue.tipCheck?.ownerLabel || 'cliente').toLowerCase()}
                                            </span>
                                          </div>
                                          <div>
                                            <strong>Almeno una mancia riuscita</strong>
                                            <div className="at-step-meta">
                                              <span className="at-owner-label">Responsabile: {venue.tipCheck?.ownerLabel || 'Cliente'}</span>
                                              {venue.tipCheck?.completedAt && <span>Completato: {formatDate(venue.tipCheck.completedAt)}</span>}
                                            </div>
                                            {venue.tipCheck?.completedAt && venue.tipCheck?.lastTipAmount !== null && venue.tipCheck?.lastTipAmount !== undefined && (
                                              <div className="at-tip-highlight">
                                                <span className="tenant-inline-label">
                                                  <Gift size={14} aria-hidden="true" />
                                                  Ultima mancia ricevuta:
                                                </span>{' '}
                                                <strong>{formatCurrencyAmount(venue.tipCheck.lastTipAmount, venue.tipCheck?.lastTipCurrency)}</strong>
                                                {venue.tipCheck?.lastTipChannel && (
                                                  <span> via {venue.tipCheck.lastTipChannel === 'TIP_LINK_WEB' ? 'QR / Tip Link' : 'Kiosk'}</span>
                                                )}
                                              </div>
                                            )}
                                            {venue.tipCheck?.blockingReason && venue.tipCheck?.status !== 'completed' && (
                                              <p className="at-step-blocking">{venue.tipCheck.blockingReason}</p>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </article>
                                  );
                                })}
                              </div>
                            )}
                          </section>
                        ))}
                      </div>
                    ) : (
                      <div className="at-detail-empty">Nessuna sezione tracker disponibile.</div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="at-drawer-header">
                    <h2 id="activation-tracker-drawer-title">Tracker attivazione</h2>
                    <button className="at-drawer-close" type="button" onClick={closeTrackerDrawer} aria-label="Chiudi tracker">
                      <X size={18} />
                    </button>
                  </div>
                  <div className="at-detail-empty">Nessun dettaglio disponibile.</div>
                </>
              )}
            </div>
          </aside>
        </>
      )}

      {contactCardTenant && (
        <div
          role="presentation"
          onClick={closeContactCard}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.35)',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <aside
            role="dialog"
            aria-modal="true"
            aria-labelledby="tenant-contact-card-title"
            onClick={(event) => event.stopPropagation()}
            style={{
              width: 'min(420px, 96vw)',
              borderRadius: 14,
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              boxShadow: '0 20px 50px rgba(2, 6, 23, 0.25)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '14px 16px',
                background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  Biglietto Contatti
                </div>
                <h3 id="tenant-contact-card-title" style={{ margin: '4px 0 0', fontSize: 18, color: '#0f172a' }}>
                  {contactCardTenant.companyName}
                </h3>
              </div>
              <button
                type="button"
                onClick={closeContactCard}
                aria-label="Chiudi biglietto contatti"
                style={{
                  border: '1px solid #cbd5e1',
                  background: '#ffffff',
                  borderRadius: 8,
                  width: 30,
                  height: 30,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#334155',
                }}
              >
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: 16, display: 'grid', gap: 10 }}>
              <div style={{ display: 'grid', gap: 6 }}>
                <small className="tenant-inline-label" style={{ color: '#64748b', fontWeight: 700 }}><Contact size={13} aria-hidden="true" /> Referente tenant</small>
                <strong style={{ color: '#0f172a', fontSize: 14 }}>{contactCardTenant.ownerFullName || '-'}</strong>
              </div>

              <div style={{ display: 'grid', gap: 6 }}>
                <small className="tenant-inline-label" style={{ color: '#64748b', fontWeight: 700 }}><User size={13} aria-hidden="true" /> Email user</small>
                <strong style={{ color: '#0f172a', fontSize: 14 }}>{contactCardTenant.userEmail || '-'}</strong>
              </div>

              <div style={{ display: 'grid', gap: 6 }}>
                <small className="tenant-inline-label" style={{ color: '#64748b', fontWeight: 700 }}><Mail size={13} aria-hidden="true" /> Email business</small>
                <strong style={{ color: '#0f172a', fontSize: 14 }}>{contactCardTenant.businessEmail || '-'}</strong>
              </div>

              <div style={{ display: 'grid', gap: 6 }}>
                <small className="tenant-inline-label" style={{ color: '#64748b', fontWeight: 700 }}><Phone size={13} aria-hidden="true" /> Telefono</small>
                <strong style={{ color: '#0f172a', fontSize: 14 }}>{contactCardTenant.phoneNumber || '-'}</strong>
              </div>

              <div style={{ display: 'grid', gap: 6 }}>
                <small className="tenant-inline-label" style={{ color: '#64748b', fontWeight: 700 }}><MapPin size={13} aria-hidden="true" /> Locali</small>

                {contactCardLoading ? (
                  <span style={{ color: '#64748b', fontSize: 13 }}>Caricamento locali...</span>
                ) : contactCardError ? (
                  <span style={{ color: '#b91c1c', fontSize: 13 }}>{contactCardError}</span>
                ) : contactCardTenant.venues?.length ? (
                  <div style={{ display: 'grid', gap: 6, maxHeight: 190, overflowY: 'auto', paddingRight: 4 }}>
                    {contactCardTenant.venues.map((venue) => (
                      <div key={venue.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>{venue.name || 'Locale'}</div>
                        <div style={{ fontSize: 13, color: '#0f172a' }}>{venue.address}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span style={{ color: '#64748b', fontSize: 13 }}>Nessun locale associato.</span>
                )}
              </div>

              <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 999, padding: '3px 8px' }}>
                  Status: {contactCardTenant.tenantStatus}
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 999, padding: '3px 8px' }}>
                  P.IVA: {contactCardTenant.pIva || '-'}
                </span>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default Tenants;
